import { Menu, Button, Drawer, Badge } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import Sider from "antd/es/layout/Sider";
import { ReactElement, useEffect, useState } from "react";
import { color } from "theme/Color";
import "./Sidebar.css";
import { NotificationIcon } from "../../../assets/icons/NotificationIcon";
import { SettingsIcon } from "../../../assets/icons/SettingsIcon";
import { BarsIcon } from "../../../assets/icons/Bars";
import Footer from "../components/Footer";
import { useAppSelector } from "../store/hooks";
import { typography } from "theme/Typography";
import { CloseOutlined, MoneyCollectOutlined, DollarOutlined } from "@ant-design/icons";
import { GroupIcon } from "../../../assets/icons/Group";
import { PlatformAdminIcon } from "../../../assets/icons/PlatformAdminIcon";
import { SummarizeIcon } from "../../../assets/icons/SummarizeIcon";
import { CircleIcon } from "../../../assets/icons/CircleIcon";
import { useMobile } from "../hooks/useMobile";
import { VehicleIcon } from "../../../assets/icons/VehicleIcon";
import { ShipperIcon } from "../../../assets/icons/ShipperIcon";
import { LiveTrackingIcon } from "../../../assets/icons/LiveTrackingIcon";
import { PaymentIcon } from "../../../assets/icons/PaymentIcon";
import { NotificationPlusIcon } from "../../../assets/icons/NotificationPlusIcon";
import Notifications from "notifications/presentation/pages/Notifications";
import { VehicleCategoryIcon } from "../../../assets/icons/VehicleCategoryIcon";
import { TripIcon } from "../../../assets/icons/TripIcon";
import { ContractIcon } from "../../../assets/icons/ContractIcon";
import _ from "lodash";
import { OrderIcon } from "../../../assets/icons/OrderIcon";
import { EuroCircleOutlined, UserOutlined, IssuesCloseOutlined, SyncOutlined, UsergroupDeleteOutlined } from "@ant-design/icons";
import { isShipper, isStaff, isVehicleOwner } from "../constants/Auth";
import { SubscriptionIcon } from "../../../assets/icons/Subscription";
import { CheckpointIcon } from "../../../assets/icons/CheckpointIcon";

export interface MenuItems {
  key: string;
  href: string;
  icon: JSX.Element;
  children?: ReactElement[];
  permission: string;
  label: string;
}

interface AMenuItem extends MenuItems {
  type?: "group";
}

type KeyLabelObject = {
  [key: string]: React.ReactNode;
};

var keyLabelObject: KeyLabelObject = {};

function getItem(label: React.ReactNode, key: React.Key, icon?: React.ReactNode, href?: string, children?: AMenuItem[], type?: "group"): AMenuItem {
  return {
    key,
    icon,
    href,
    children,
    label,
    type,
  } as AMenuItem;
}

const SidebarSkeleton: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const shimmer: React.CSSProperties = {
    background: "linear-gradient(90deg, #f0f0f0 25%, #b8b6b6 37%, #f0f0f0 63%)",
    backgroundSize: "400% 100%",
    animation: "sidebar-skeleton-shimmer 1.4s ease infinite",
    borderRadius: 6,
  };

  const Row: React.FC<{ width?: string }> = ({ width = "60%" }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "0 20px",
        height: 42,
        flexShrink: 0,
      }}
    >
      <div style={{ ...shimmer, width: 32, height: 32, borderRadius: 5, flexShrink: 0 }} />
      <div style={{ ...shimmer, width, height: 32 }} />
    </div>
  );

  const getRandomWidth = (min = 45, max = 78) => `${Math.floor(Math.random() * (max - min + 1)) + min}%`;

  const topRowWidths = Array.from({ length: 10 }, () => getRandomWidth());
  const bottomRowWidths = Array.from({ length: 3 }, () => getRandomWidth(45, 65));

  return (
    <div
      style={{
        position: "fixed",
        top: 0,        
        left: 0,           
        width: isMobile ? "100%" : 380,
        height: "100vh",
        background: color.WHITE,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        boxSizing: "border-box",
        zIndex: 5,
      }}
    >
      <div style={{ padding: "1.5rem 1rem", flexShrink: 0 }}>
        <div style={{ ...shimmer, width: 120, height: 32 }} />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "hidden", paddingTop: "0.5rem" }}>
        {topRowWidths.map((w, i) => (
          <Row key={`top-${i}`} width={w} />
        ))}
      </div>

      <div style={{ flexShrink: 0, paddingBottom: "0.5rem" }}>
        {bottomRowWidths.map((w, i) => (
          <Row key={`bottom-${i}`} width={w} />
        ))}
      </div>

      <div
        style={{
          borderTop: `1px solid ${color.HR_GREY}`,
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ ...shimmer, width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ ...shimmer, width: 110, height: 13 }} />
            <div style={{ ...shimmer, width: 75, height: 11 }} />
          </div>
        </div>
        <div style={{ ...shimmer, width: 18, height: 18, borderRadius: 4 }} />
      </div>
    </div>
  );
};
export const Sidebar: React.FC<any> = (props) => {
  const isMobile = useMobile();

  const { collapsed, setCollapsed } = props;
  const { userProfile: storedUserProfile, cachedSidebarPermissions } = useAppSelector((state) => state.user);


  const effectiveProfile = storedUserProfile?.user
  ? storedUserProfile
  : cachedSidebarPermissions
  ? {
      user: {
        roles: cachedSidebarPermissions.roles,
        permissions: cachedSidebarPermissions.permissions,
        staff: cachedSidebarPermissions.staff,
      },
      tripAccess: cachedSidebarPermissions.tripAccess,
    }
  : null;
  const userProfile = effectiveProfile;

  const userRole = effectiveProfile?.user?.roles?.[0]?.roleCode;

  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKey, setSelectedKey] = useState<string>("1");
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const createdRole = _.filter(effectiveProfile?.user?.roles, { systemDefault: 0 });
  const { unReadNotification } = useAppSelector((state) => state?.notification);

  const isCreatedRole = createdRole.length > 0;

  const tripItems = [getItem(userRole === "VEHICLE_OWNER" ? "Created" : "All Trips", "222", <CircleIcon style={{ width: "12pt" }} />, "/trip"), getItem("Completed", "333", <CircleIcon style={{ width: "12pt" }} />, "/completed-trip")];

  if (isVehicleOwner(effectiveProfile)) {
    tripItems.push(getItem("Schedule Trip", "444", <CircleIcon style={{ width: "12pt" }} />, "/trip-schedule"));
  }

  const items = [
    getItem("Dashboards", "1", <BarsIcon />, "/"),
    (userProfile?.user?.permissions.includes("bid_request:list_all") || userProfile?.user?.permissions?.includes("bid_request:list")) && getItem(userRole === "VEHICLE_OWNER" ? "Bids" : "Requests and Bids", "2", <PaymentIcon />, "/orders"),
    (userProfile?.user?.permissions.includes("order:list") || userProfile?.user?.permissions.includes("order:list_all")) && getItem("Orders", "42", <OrderIcon />, "/order"),

    getItem(
      <div style={{ position: "relative", display: "inline-block" }}>
        Notification
        {unReadNotification > 0 && (
          <Badge
            count={unReadNotification}
            style={{
              position: "absolute",
              top: "-16px",
              right: "-36px",
              backgroundColor: "#52c41a",
              fontSize: "10px",
            }}
            color={"#dd283d"}
          />
        )}
      </div>,
      "3",
      <NotificationIcon />,
      "",
    ),
    userRole !== "SUPER_ADMIN" && getItem("My Profile", "45", <UserOutlined style={{ fontSize: "20px" }} />, "/user-profile"),
    getItem("Settings", "4", <SettingsIcon />, "/settings?tab=password"),
    userProfile?.user?.permissions.includes("vehicle_owner:list_all") && getItem("Vehicle Owner", "11", <VehicleIcon />, "/vehicle-owner"),
    userProfile?.user?.permissions.includes("vehicle_shift:list_all") && getItem("Vehicle Shift Requests", "28", <SyncOutlined style={{ fontSize: "22px" }} />, "/vehicle-shift-requests"),
    userProfile?.user?.permissions.includes("re_bid:list_all") && getItem("Canceled Bids", "46", <EuroCircleOutlined style={{ fontSize: "22px" }} />, "/order-cancel"),
    ((userRole === "SHIPPER" && userProfile?.tripAccess) || (userRole?.startsWith("STAFF_") && (userProfile?.user?.permissions?.includes("trip:list") || userProfile?.user?.permissions?.includes("trip:list_all")) && userProfile?.user?.staff?.shipperTripAccess) || (userRole !== "SHIPPER" && !userRole?.startsWith("STAFF_") && (userProfile?.user?.permissions?.includes("trip:list") || userProfile?.user?.permissions?.includes("trip:list_all")))) && getItem("Trip ", "26", <TripIcon />, "", tripItems),
    userProfile?.user?.permissions.includes("shipper:list_all") && getItem("Shipper", "12", <ShipperIcon />, "/shipper"),
    userProfile?.user?.permissions.includes("user:list_all") && getItem(isShipper(userProfile) || isStaff(userProfile) ? "Staff" : "Users", "6", <PlatformAdminIcon />, "/users"),
    (userProfile?.user?.permissions.includes("driver:list") || userProfile?.user?.permissions.includes("driver:list_all")) && getItem("Drivers", "5", <GroupIcon />, "/driver"),
    (isCreatedRole || userProfile?.user?.permissions.includes("device_location:list")) && userRole !== "SHIPPER" && !userRole?.startsWith("STAFF_") && getItem("Live Tracking", "13", <LiveTrackingIcon />, "/live-tracking"),
    userProfile?.user?.permissions.includes("role:list_all") && getItem("Roles and Permissions", "14", <GroupIcon />, "/roles"),
    getItem("Customized Notification", "20", <NotificationPlusIcon />, "/customized-notifications-shipper", [getItem("Shipper", "21", <CircleIcon style={{ width: "12pt" }} />, "/customized-notifications-shipper"), getItem("Vehicle Owner", "22", <CircleIcon style={{ width: "12pt" }} />, "/customized-notifications-vehicle-owner"), getItem("Driver", "23", <CircleIcon style={{ width: "12pt" }} />, "/customized-notifications-driver")]),
    userProfile?.user?.permissions.includes("subscription_table:list_all") && getItem("Subscriptions", "108", <SubscriptionIcon />, "/subscriptions"),

    userProfile?.user?.permissions.includes("vehicle:list") &&
      userRole === "VEHICLE_OWNER" &&
      getItem("Vehicle ", "25", <VehicleCategoryIcon />, "", [
        getItem("Vehicle", "100", <CircleIcon style={{ width: "12pt" }} />, "/vehicle"),
        getItem("Pending", "46", <CircleIcon style={{ width: "12pt" }} />, "/pending-vehicle"),
        getItem("Approval", "48", <CircleIcon style={{ width: "12pt" }} />, "/approval"),
        getItem("Edit Requested", "16", <CircleIcon style={{ width: "12pt" }} />, "/edit-requested-vehicle"),
        // getItem("Awaiting Approval", "17", <CircleIcon style={{ width: "12pt" }} />, "/awaiting-admin-approval-vehicle"),
      ]),
    (userProfile?.user?.permissions.includes("vehicle:list_all") || userProfile?.user?.permissions.includes("vehicle_category:list_all")) &&
      isCreatedRole &&
      getItem("Vehicles", "24", <VehicleCategoryIcon />, "", [
        userProfile?.user?.permissions.includes("vehicle:list_all") && getItem("Vehicle", "40", <CircleIcon style={{ width: "12pt" }} />, "/vehicle"),
        userProfile?.user?.permissions.includes("vehicle_category:list_all") && getItem("Vehicle Category", "15", <CircleIcon style={{ width: "12pt" }} />, "/vehicle-category"),
        userProfile?.user?.permissions.includes("vehicle_type:list_all") && getItem("Vehicle Type", "16", <CircleIcon style={{ width: "12pt" }} />, "/vehicle-type"),
        userProfile?.user?.permissions.includes("tqms:list_all") && getItem("Awaiting Approval Vehicle", "17", <CircleIcon style={{ width: "12pt" }} />, "/awaiting-approval-vehicle"),
        userProfile?.user?.permissions.includes("tqms:list_all") && getItem("Edit Request Vehicle", "18", <CircleIcon style={{ width: "12pt" }} />, "/edit-requested-vehicle"),
        userProfile?.user?.permissions.includes("tqms:list_all") && getItem("Rejected Vehicle", "19", <CircleIcon style={{ width: "12pt" }} />, "/rejected-vehicle"),
      ]),
    userProfile?.user?.permissions.includes("vehicle:list_all") &&
      getItem("Pending Verification", "34", <ContractIcon />, "", [
        userProfile?.user?.permissions.includes("tqms:list_all") && getItem("Vehicle Owner", "35", <CircleIcon style={{ width: "12pt" }} />, "/tqms-vehicle-owner"),
        userProfile?.user?.permissions.includes("tqms:list_all") && getItem("TQMS Pending Vehicle", "36", <CircleIcon style={{ width: "12pt" }} />, "/tqms-pending-vehicle-all"),
        // userProfile?.user?.permissions.includes("tqms:list_all") && getItem("Load-Board Pending Vehicle", "37", <CircleIcon style={{ width: "12pt" }} />, "/lodbod-pending-vehicle"),
      ]),

    userRole !== "SHIPPER" && userRole !== "VEHICLE_OWNER" && userProfile?.user?.permissions.includes("digital_contract:update") && getItem("Contract", "25", <ContractIcon />, "", [userProfile?.user?.permissions.includes("digital_contract:update") && getItem("Digital Contract", "311", <CircleIcon style={{ width: "12pt" }} />, "/digital-contract")]),
    userProfile?.user?.permissions.includes("account_approval:list_all") && getItem("Awaiting Account Approval", "33", <IssuesCloseOutlined style={{ fontSize: "22px" }} />, "/awaiting-account-approval"),
    userRole?.startsWith("PLATFORM_ADMIN") && (userProfile?.user?.permissions.includes("account_deletion_request:list_all")) && getItem("User Deletion Request", "53", <UsergroupDeleteOutlined style={{ fontSize: "18pt" }} />, "/user-delete-request-report"),
    userRole === "VEHICLE_OWNER" && getItem("Virtual Checkpoint", "49", <CheckpointIcon />, "/virtual-checkpoint"),
    userProfile?.user?.permissions.includes("refund_policy:list_all") && getItem("Refund Request", "102", <MoneyCollectOutlined style={{ fontSize: "22px" }} />, "/refund-request"),
    ((userRole === "SHIPPER" && userProfile?.tripAccess) || (userRole?.startsWith("STAFF_") && userProfile?.user?.permissions.includes("shipment_tracking:list_all"))) && getItem("Shipment Tracking", "138", <LiveTrackingIcon />, "/shipment-tracking"),
    (userProfile?.user?.permissions.includes("promotion_code:list_all") || isVehicleOwner(userProfile)) && getItem("Promotions", "103", <DollarOutlined style={{ fontSize: "22px" }} />, "/promotions"),
    (userProfile?.user?.permissions.includes("report:list_all") || userProfile?.user?.permissions.includes("shipment_tracking_report:list_all") || userProfile?.user?.permissions.includes("shipment_calendar_report:list_all") || userProfile?.user?.permissions.includes("shipment_dispatch_report:list_all") || userRole === "SHIPPER" || userRole === "VEHICLE_OWNER") &&
      getItem("Reports", "7", <SummarizeIcon />, "", [
        userProfile?.user?.permissions.includes("report:list_all") && getItem("Vehicle Owner Report", "8", <CircleIcon style={{ width: "12pt" }} />, "/vehicle-owner-report"),
        userProfile?.user?.permissions.includes("report:list_all") && getItem("Driver Report", "9", <CircleIcon style={{ width: "12pt" }} />, "/driver-report"),
        userProfile?.user?.permissions.includes("report:list_all") && getItem("Vehicle Report", "10", <CircleIcon style={{ width: "12pt" }} />, "/vehicle-report"),
        userRole === "VEHICLE_OWNER" && getItem("Expiry Report", "30", <CircleIcon style={{ width: "12pt" }} />, "/expiry-report"),
        userRole === "VEHICLE_OWNER" && getItem("Vehicle Log History", "31", <CircleIcon style={{ width: "12pt" }} />, "/vehicle-history"),
        userRole === "VEHICLE_OWNER" && getItem("GPS Report", "32", <CircleIcon style={{ width: "12pt" }} />, "/GPS-report"),
        userRole === "VEHICLE_OWNER" && getItem("Trip Report", "43", <CircleIcon style={{ width: "12pt" }} />, "/trip-report"),
        userRole === "VEHICLE_OWNER" && getItem("Driver Report", "44", <CircleIcon style={{ width: "12pt" }} />, "/driver-reports"),
        userProfile?.user?.permissions.includes("report:list_all") && getItem("Vehicle Checklist", "52", <CircleIcon style={{ width: "12pt" }} />, "/vehicle-Checklist-report"),
        userProfile?.user?.permissions.includes("report:list_all") && getItem("User Log Report", "51", <CircleIcon style={{ width: "12pt" }} />, "/user-log-report"),
        (userRole === "SHIPPER" || userProfile?.user?.permissions.includes("shipment_tracking_report:list_all")) && getItem("Shipment Tracking Report", "38", <CircleIcon style={{ width: "12pt" }} />, "/shipment-tracking-report"),
        (userRole === "SHIPPER" || userProfile?.user?.permissions.includes("shipment_calendar_report:list_all")) && getItem("Shipment Calendar Report", "39", <CircleIcon style={{ width: "12pt" }} />, "/shipment-calender"),
        (userRole === "SHIPPER" || userProfile?.user?.permissions.includes("shipment_dispatch_report:list_all")) && getItem("Shipment Dispatch Report", "41", <CircleIcon style={{ width: "12pt" }} />, "/shipment-dispatch"),
        userProfile?.user?.permissions.includes("report:list_all") && getItem("LODBOD TQMS Sync", "31", <CircleIcon style={{ width: "12pt" }} />, "/server-logs-report"),

        userProfile?.user?.permissions.includes("report:list_all") && userRole?.startsWith("PLATFORM_ADMIN") && getItem("Blocked User", "101", <CircleIcon style={{ width: "12pt" }} />, "/blocked-user-report"),

        userProfile?.user?.permissions.includes("report:list_all") && getItem("Payment Report", "482", <CircleIcon style={{ width: "12pt" }} />, "/payment-report"),
        userProfile?.user?.permissions.includes("payment_commission:list_all") && getItem("Payment Commission Report", "483", <CircleIcon style={{ width: "12pt" }} />, "/payment-commission-report"),
        userRole === "VEHICLE_OWNER" && getItem("Point Transaction Report", "310", <CircleIcon style={{ width: "12pt" }} />, "/point-report"),
      ]),
  ].filter((item) => hasRolePermission(item, userRole));

  function hasRolePermission(item: AMenuItem, userRole: any): boolean {
    switch (item?.label) {
      case "Drivers":
        return userProfile?.user?.permissions.includes("driver:list") || userProfile?.user?.permissions.includes("driver:list_all");
      case "Customized Notification":
        return userProfile?.user?.permissions.includes("customize_notification:detail");
      case "Trip":
        return userProfile?.user?.permissions.includes("trip:list") || userProfile?.user?.permissions.includes("trip:list_all");
      case "Vehicle Owner":
        return userProfile?.user?.permissions.includes("vehicle_owner:list_all");
      case "Canceled Bids":
        return userProfile?.user?.permissions.includes("re_bid:list_all");
      case "Reports":
        return userRole === "SHIPPER" || userRole?.startsWith("STAFF_") || userRole === "VEHICLE_OWNER" || userProfile?.user?.permissions.includes("report:list_all");
      case "Vehicle Owner Report":
        return userProfile?.user?.permissions.includes("vehicle_owner_report:list");
      case "Driver Report":
        return userProfile?.user?.permissions.includes("driver_report:list");
      case "Vehicle Report":
        return userProfile?.user?.permissions.includes("vehicle_report:list");
      case "Shipper":
        return userProfile?.user?.permissions.includes("shipper:list_all");
      case "Live Tracking":
        return userProfile?.user?.permissions.includes("device_location:list_all") || userProfile?.user?.permissions.includes("device_location:list");
      case "Roles and Permissions":
        return userProfile?.user?.permissions.includes("role:list_all");
      case "Requests and Bids":
        return userProfile?.user?.permissions.includes("bid_request:list_all") || userProfile?.user?.permissions.includes("bid_request:list") || userProfile?.user?.permissions.includes("order:list");
      case "Vehicles":
        return userProfile?.user?.permissions.includes("vehicle:list_all") || (userProfile?.user?.permissions.includes("vehicle_category:list_all") && isCreatedRole);
      case "Vehicle":
        return userProfile?.user?.permissions.includes("vehicle:list");
      case "Vehicle Category":
        return userProfile?.user?.permissions.includes("vehicle_category:detail");
      case "Vehicle Type":
        return userProfile?.user?.permissions.includes("vehicle_type:list_all");
      case "Settings":
        return userProfile?.user?.permissions.includes("profile:update");
      default:
        return true;
    }
  }

  const onClick = ({ key, item }: any) => {
    const label = keyLabelObject[item.key];
    setSelectedKey(key); // Update state
    localStorage.setItem("selectedMenuKey", key); // Persist selection
    if (key === "3") {
      setNotificationVisible(true);
    } else {
      navigate(item?.props?.href, { state: { label: label } });
    }
  };

  const findSelectedMenuItem = (menuItems: any, cb: any) => {
    const routeParts = location.pathname.split("/");
    const firstRoutePart = routeParts[1];

    menuItems.forEach((menuItem: AMenuItem) => {
      if (menuItem?.children) {
        return findSelectedMenuItem(menuItem.children, cb);
      }
      if (menuItem?.href === `/${firstRoutePart}`) {
        return cb(menuItem);
      }
    });
  };

  const findParentKeys = (menuItems: AMenuItem[], key: string, parentKeys: string[] = []): string[] | null => {
    for (let menuItem of menuItems) {
      if (menuItem?.key === key) {
        return parentKeys;
      }
      if (menuItem?.children) {
        const foundParentKeys = findParentKeys(menuItem?.children as any, key, [...parentKeys, menuItem?.key]);
        if (foundParentKeys) {
          return foundParentKeys;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    findSelectedMenuItem(items, (selectedItem: AMenuItem) => {
      if (selectedItem) {
        setSelectedKey(selectedItem?.key);
        localStorage.setItem("selectedMenuKey", selectedItem.key); // Persist selection
        const parentKeys = findParentKeys(items, selectedItem?.key);
        if (parentKeys) {
          setOpenKeys(parentKeys);
        }
      } else {
        setSelectedKey("1");
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    const savedKey = localStorage.getItem("selectedMenuKey");
    if (savedKey) {
      setSelectedKey(savedKey);
    }
  }, []); // Empty dependency array ensures this runs only on mount

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  if (!effectiveProfile) {
    return <SidebarSkeleton isMobile={isMobile} />;
  }
  const topItems = items.filter((item) => item?.key !== "3" && item?.key !== "4" && item?.key !== "45");
  const bottomItems = items.filter((item) => item?.key === "3" || item?.key === "45" || item?.key === "4");

  return isMobile ? (
    <>
      <Drawer title="Menu" placement="left" closable={false} onClose={() => setCollapsed(true)} open={!collapsed} bodyStyle={{ padding: 0 }} headerStyle={{ display: "none" }}>
        <>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={() => setCollapsed(collapsed)}
            style={{
              position: "absolute",
              top: "20px",
              right: "10px",
              zIndex: 1,
            }}
          />
          <div
            style={{
              padding: "1.5rem 0.5rem",
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          >
            <img width={"120px"} style={{ marginLeft: "15px" }} src={require("../../../assets/images/lodbodlogo.png")} />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "90vh",
            }}
          >
            <Menu
              onClick={onClick}
              mode="inline"
              selectedKeys={[selectedKey]}
              openKeys={openKeys}
              onOpenChange={handleOpenChange}
              defaultSelectedKeys={["1"]}
              items={topItems}
              className="custom-sidebar-menu"
              style={{
                width: "max-content !important",
                paddingBottom: "30px",
                fontSize: typography.fontSizes.md,
                fontWeight: typography.fontWeights.semibold,
                background: "white",
                color: color.SIDEBAR_TEXT,
              }}
            />
            <div>
              <Menu
                onClick={onClick}
                mode="inline"
                selectedKeys={[selectedKey]}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                defaultSelectedKeys={["1"]}
                items={bottomItems}
                className="custom-sidebar-menu"
                style={{
                  width: "max-content !important",
                  paddingBottom: "10px",
                  background: "white",
                  fontSize: typography.fontSizes.md,
                  fontWeight: typography.fontWeights.semibold,
                  color: color.SIDEBAR_TEXT,
                }}
              />

              <Footer />
            </div>
          </div>
        </>
      </Drawer>
      <Drawer placement="right" closable={false} onClose={() => setNotificationVisible(false)} open={notificationVisible} width="40%" style={{ zIndex: 1001, background: "#fff" }}>
        <Notifications notificationVisible={notificationVisible} setNotificationVisible={setNotificationVisible} />
      </Drawer>
    </>
  ) : (
    // ) : (
    <>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={380}
        style={{
          overflow: "auto",
          height: "100vh",
          position: "sticky",
          zIndex: "5",
          left: 0,
          top: 0,
          bottom: 0,
          backgroundColor: color.WHITE,
        }}
      >
        {!collapsed && (
          <>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setCollapsed(collapsed)}
              style={{
                position: "absolute",
                top: "20px",
                right: "10px",
                zIndex: 1,
              }}
            />
            <div
              style={{
                padding: "1.5rem 0.5rem",
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
            >
              <img width={"120px"} style={{ marginLeft: "15px" }} src={require("../../../assets/images/lodbodlogo.png")} />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "90vh",
              }}
            >
              <Menu
                onClick={onClick}
                mode="inline"
                selectedKeys={[selectedKey]}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                defaultSelectedKeys={["1"]}
                className="custom-sidebar-menu"
                items={topItems}
                style={{
                  width: "max-content !important",
                  paddingBottom: "30px",
                  fontSize: typography.fontSizes.md,
                  fontWeight: typography.fontWeights.semibold,
                  background: "white",
                  color: color.SIDEBAR_TEXT,
                }}
              />
              <div>
                <Menu
                  onClick={onClick}
                  mode="inline"
                  selectedKeys={[selectedKey]}
                  openKeys={openKeys}
                  onOpenChange={handleOpenChange}
                  defaultSelectedKeys={["1"]}
                  items={bottomItems}
                  className="custom-sidebar-menu"
                  style={{
                    width: "max-content !important",
                    paddingBottom: "10px",
                    background: "white",
                    fontSize: typography.fontSizes.md,
                    fontWeight: typography.fontWeights.semibold,
                    color: color.SIDEBAR_TEXT,
                  }}
                />

                <Footer />
              </div>
            </div>
          </>
        )}
      </Sider>
      <Drawer placement="right" closable={false} onClose={() => setNotificationVisible(false)} open={notificationVisible} width="40%" style={{ zIndex: 1001, background: "#fff" }}>
        <Notifications notificationVisible={notificationVisible} setNotificationVisible={setNotificationVisible} />
      </Drawer>
    </>
  );
};

export default Sidebar;
